// Library imports
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// User Component imports
import { PageContent, PageHeader, PageWrapper, PageContainer } from 'components/common/layout_element/LayoutElement';
import { Row, Button, Stack, Card, Pagination, Icon, CloseButton } from "_core/components/common/UI";
import { CustomDateRangePicker, DateRadio, FitList } from "components/common/parts";
import * as Constants from "_core/configs/Constants";
import { setLoading, setErrorModal, setInitializeState, setMenuState } from "_core/slices/scene/SceneSlice";
import AdminActionLogListApi from "apis/AdminActionLogListApi";
import Encoding from 'encoding-japanese';

import './AdminUserActionLog.scss';

const PAGE_SIZE = 20;

// 時刻の上限。終了日時の既定値(その日の終わり)にも使う
const MAX_HOUR = 23;
const MAX_MINUTE = 59;

// 時刻リストの刻み(分)。カレンダーでは1時間単位で選び、分まで指定したい場合は日時の入力欄に直接打ち込む
const TIME_INTERVAL_MINUTES = 60;
const MINUTES_IN_DAY = 24 * 60;

// 入力欄の表示形式(yyyy-mm-dd HH:mm)
// toISOString() はUTC基準のため、JSTの早朝に日付が1日ずれる。送信側(buildRequestParams)と基準を揃えてローカル時刻で組み立てる
const formatDateTimeForInput = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day} ${hh}:${mm}`;
}

/**
 * 入力欄の文字列を Date に変換する
 * new Date(文字列) は "2026-08-19 10:30" の解釈がブラウザ依存のため、自前でパースする
 * @param {string} text 入力値(時刻は省略可)
 * @param {number} fallbackHour 時刻が省略されたときに使う時
 * @param {number} fallbackMinute 時刻が省略されたときに使う分
 * @returns {Date|null} 変換できない場合は null
 */
const parseDateTimeInput = (text, fallbackHour, fallbackMinute) => {
    const matched = String(text).trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{1,2}))?$/);
    if (!matched) {
        return null;
    }
    const [, year, month, day, hourText, minuteText] = matched;
    const hour = hourText === undefined ? fallbackHour : Number(hourText);
    const minute = minuteText === undefined ? fallbackMinute : Number(minuteText);
    if (hour > MAX_HOUR || minute > MAX_MINUTE) {
        return null;
    }
    const date = new Date(Number(year), Number(month) - 1, Number(day), hour, minute, 0, 0);
    // 2026-02-31 のような存在しない日付は Date が繰り上げてしまうため、入力と一致するかで弾く
    if (date.getFullYear() !== Number(year) || date.getMonth() !== Number(month) - 1 || date.getDate() !== Number(day)) {
        return null;
    }
    return date;
}

// 検索条件の既定値。開始は1週間前の 00:00、終了は当日の 23:59
const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    date.setHours(0, 0, 0, 0);
    return date;
}

const getDefaultEndDate = () => {
    const date = new Date();
    date.setHours(MAX_HOUR, MAX_MINUTE, 0, 0);
    return date;
}

/**
 * 終了日時の時刻候補を作る
 * 開始日時の刻みの1分前に揃えることで、「9:00〜9:59」のように区間の終わりが閉じ、
 * 次の区間(10:00〜)と重ならないようにする(終了時刻の秒は59まで含めるため)
 * injectTimes に渡した Date はそのまま onChange へ流れるので、選択中の日付を引き継いで組み立てる
 * @param {Date} baseDate 選択中の日付
 * @returns {Array<Date>} 時刻候補(00:59, 01:59, ... 23:59)
 */
const buildEndTimeOptions = (baseDate) => {
    const options = [];
    for (let minutes = TIME_INTERVAL_MINUTES; minutes <= MINUTES_IN_DAY; minutes += TIME_INTERVAL_MINUTES) {
        const option = new Date(baseDate);
        option.setHours(0, minutes - 1, 0, 0);
        options.push(option);
    }
    return options;
}

const formatDate = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}年${m}月${day}日`;
};

const formatDateTime = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${y}年${m}月${day}日 ${hh}:${mm}:${ss}`;
};

const AdminUserActionLog = () => {
    const dispatch = useDispatch();
    const CallAdminActionLogListApi = AdminActionLogListApi();
    const { initializeState } = useSelector((store) => store.scene);

    // 一覧用
    const [page, setPage] = useState(1);
    const [maxPage, setMaxPage] = useState(1);
    const [isListLoading, setIsListLoading] = useState(true);

    // 日時範囲
    const [startDateInput, setStartDateInput] = useState(() => formatDateTimeForInput(getDefaultStartDate()));
    const [endDateInput, setEndDateInput] = useState(() => formatDateTimeForInput(getDefaultEndDate()));
    const [startDate, setStartDate] = useState(getDefaultStartDate);
    const [endDate, setEndDate] = useState(getDefaultEndDate);

    // 終了日時の時刻候補。選んだ日付をそのまま引き継がせるため、endDate に追従させる
    const endTimeOptions = useMemo(() => buildEndTimeOptions(endDate), [endDate]);

    // 表示終了日は当日。未来のログは存在しないため、翌日以降は選べないようにする
    const maxDate = new Date();

    // イベント名検索
    const [eventNameDict, setEventNameDict] = useState({});
    const [selectedEventNames, setSelectedEventNames] = useState([]);

    // カレンダーダイアログ
    const [isStartDialogVisible, setIsStartDialogVisible] = useState(false);
    const startDialogRef = useRef(null);
    const [isEndDialogVisible, setIsEndDialogVisible] = useState(false);
    const endDialogRef = useRef(null);

    // アカウント名検索(操作ログに記録があるアカウントのみサーバーから返る)
    const [accountNameDict, setAccountNameDict] = useState({});
    const [selectedTAccountIds, setSelectedTAccountIds] = useState([]);

    // 選択肢は一覧APIのレスポンスで届くため、最初に届いた時点で「すべて」選択済みの状態にする
    // (未選択のまま全件が返ると、チェックの見た目と結果が食い違うため)
    const isFilterInitializedRef = useRef(false);

    // 一覧データ
    const [actionLogListData, setActionLogListData] = useState({
        columsize: { account_name: 2, event_name: 4, regdate: 2, detail: 1 },
        title: {
            account_name: "アカウント名",
            event_name: "イベント名",
            regdate: "操作日時",
            detail: "",
        },
        value: []
    });

    // 詳細モーダル
    const [isShowDetailModal, setIsShowDetailModal] = useState(false);
    const [detailActionLog, setDetailActionLog] = useState(null);

    // 絞り込みプルダウン(details)の参照
    const accountSelectRef = useRef(null);
    const eventSelectRef = useRef(null);

    // プルダウンは領域外をクリックしたら閉じる
    // (detailsは既定だとsummary以外を押しても開いたままのため)
    useEffect(() => {
        const closeSelectOnOutsideClick = (e) => {
            [accountSelectRef, eventSelectRef].forEach((selectRef) => {
                if (selectRef.current && selectRef.current.open && !selectRef.current.contains(e.target)) {
                    selectRef.current.open = false;
                }
            });
        };
        document.addEventListener("click", closeSelectOnOutsideClick);
        return () => {
            document.removeEventListener("click", closeSelectOnOutsideClick);
        };
    }, []);

    useEffect(() => {
        dispatch(setMenuState({ isHeader: false, isMenu: true, isFooter: false, isFullScreen: false }));
    }, [dispatch]);

    useEffect(() => {
        if (initializeState !== Constants.INITIALIZE_STATE.allComplete) {
            return;
        }
        dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetching));
        loadList(1, startDate, endDate, selectedTAccountIds, selectedEventNames);
    }, [initializeState]);

    useEffect(() => {
        if (isStartDialogVisible) {
            document.addEventListener('mousedown', handleClickStartOutside);
        }
        else {
            document.removeEventListener('mousedown', handleClickStartOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickStartOutside);
        };
    }, [isStartDialogVisible]);

    const handleClickStartOutside = (event) => {
        if (startDialogRef.current && !startDialogRef.current.contains(event.target)) {
            setIsStartDialogVisible(false);
        }
    };

    useEffect(() => {
        if (isEndDialogVisible) {
            document.addEventListener('mousedown', handleClickEndOutside);
        }
        else {
            document.removeEventListener('mousedown', handleClickEndOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickEndOutside);
        };
    }, [isEndDialogVisible]);

    const handleClickEndOutside = (event) => {
        if (endDialogRef.current && !endDialogRef.current.contains(event.target)) {
            setIsEndDialogVisible(false);
        }
    };

    const topRef = useRef(null);
    const scrollToRef = (targetRef) => {
        if (targetRef.current) {
            targetRef.current.scrollIntoView({
                behavior: "smooth", // スムーズなスクロール
                block: "start",    // スクロール位置（'start', 'center', 'end' など）
            });
        }
    };

    const buildRequestParams = (targetPage, isLimit, nextStartDate, nextEndDate, nextTAccountIds, nextEventNames) => {
        const params = {
            page: targetPage,
            page_size: PAGE_SIZE,
            is_limit: isLimit ? 1 : 0,
            t_account_id_list: nextTAccountIds,
            range_start_year: 0,
            range_start_month: 0,
            range_start_day: 0,
            range_start_hour: 0,
            range_start_minute: 0,
            range_end_year: 0,
            range_end_month: 0,
            range_end_day: 0,
            // 日時未指定でも「その日の終わりまで」を既定にして、サーバー側の従来の丸めと揃える
            range_end_hour: MAX_HOUR,
            range_end_minute: MAX_MINUTE,
            event_name_list: nextEventNames
        };
        if (nextStartDate) {
            params.range_start_year = nextStartDate.getFullYear();
            params.range_start_month = nextStartDate.getMonth() + 1;
            params.range_start_day = nextStartDate.getDate();
            params.range_start_hour = nextStartDate.getHours();
            params.range_start_minute = nextStartDate.getMinutes();
        }
        if (nextEndDate) {
            params.range_end_year = nextEndDate.getFullYear();
            params.range_end_month = nextEndDate.getMonth() + 1;
            params.range_end_day = nextEndDate.getDate();
            params.range_end_hour = nextEndDate.getHours();
            params.range_end_minute = nextEndDate.getMinutes();
        }
        return params;
    };

    const loadList = async (targetPage, nextStartDate, nextEndDate, nextTAccountIds, nextEventNames) => {
        setIsListLoading(true);
        if (targetPage === 1) {
            dispatch(setLoading(true));
        }
        const params = buildRequestParams(targetPage, true, nextStartDate, nextEndDate, nextTAccountIds, nextEventNames);
        const { response } = await CallAdminActionLogListApi(params);

        if (response && response.error_code === 0) {
            setMaxPage(response.max_page || 1);
            buildListData(response.action_log_list || []);
            const nextEventNameDict = response.event_name_dict || {};
            const nextAccountNameDict = response.account_name_dict || {};
            setEventNameDict(nextEventNameDict);
            setAccountNameDict(nextAccountNameDict);
            // 選択肢が初めて届いた時点で全選択にして、「すべて」が入った状態で画面を開かせる
            if (!isFilterInitializedRef.current) {
                isFilterInitializedRef.current = true;
                setSelectedEventNames(Object.keys(nextEventNameDict));
                setSelectedTAccountIds(Object.keys(nextAccountNameDict));
            }
        } else {
            dispatch(setErrorModal({ show: true, text: response.error_detail }));
            buildListData([]);
        }
        setIsListLoading(false);
        dispatch(setLoading(false));
        dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetchComplete));
    };

    /**
     * 表示リストのデータを整形する
     * @param {Array<Object>} logs 操作ログのリスト
     */
    const buildListData = (logs) => {
        const listData = {
            columsize: { account_name: 2, event_name: 4, regdate: 2, detail: 1 },
            title: {
                account_name: "アカウント名",
                event_name: "イベント名",
                regdate: "操作日時",
                detail: "",
            },
            value: []
        };
        logs.forEach((log) => {
            const onClick = () => {
                openDetail(log);
            };
            const rowData = {
                account_name: log.account_name,
                event_name: log.name,
                regdate: formatDateTime(log.regdate),
                detail: <Button className={"operate-button secondary-border text-size-normal"} onClick={onClick}>詳細</Button>
            };
            listData.value.push({
                data: rowData,
                title: {},
                onclick: onClick,
            });
        });
        setActionLogListData(listData);
    };

    /**
     * 絞り込みが1件も選ばれていないかを判定する
     * 空配列はサーバー側で「絞り込み無し(全件)」に解釈されるため、この状態では通信せずに0件を表示する
     * @returns {boolean} 該当なしとして扱う場合は true
     */
    const isEmptyFilter = () => {
        // 選択肢が届く前は初期化待ちなので、空とはみなさない
        if (!isFilterInitializedRef.current) {
            return false;
        }
        return selectedTAccountIds.length === 0 || selectedEventNames.length === 0;
    };

    /**
     * 絞り込みが空のときに一覧を該当なしにする
     */
    const showEmptyList = () => {
        buildListData([]);
        setMaxPage(1);
        setIsListLoading(false);
        dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetchComplete));
    };

    /**
     * ページボタン押下時の処理
     * @param {number} newPage ページ番号
     */
    const handlePageChange = (newPage) => {
        setPage(newPage);
        if (isEmptyFilter()) {
            showEmptyList();
            return;
        }
        loadList(newPage, startDate, endDate, selectedTAccountIds, selectedEventNames);
    };

    /**
     * 検索ボタン押下時の処理
     */
    const handleSearch = () => {
        setPage(1);
        if (isEmptyFilter()) {
            showEmptyList();
            return;
        }
        loadList(1, startDate, endDate, selectedTAccountIds, selectedEventNames);
    };

    /**
     * 更新ボタン押下時の処理
     * 検索条件も表示中のページも変えずに取り直す
     */
    const handleReload = () => {
        if (isEmptyFilter()) {
            showEmptyList();
            return;
        }
        loadList(page, startDate, endDate, selectedTAccountIds, selectedEventNames);
    };

    // 詳細モーダルを開く
    const openDetail = async (actionLog) => {
        setDetailActionLog(actionLog);
        setIsShowDetailModal(true);
    };

    /**
     * 操作ログの操作内容をデータをCSV向けに整形する
     * @param {Object} actionLog 操作ログ
     * @returns 操作内容
     */
    const getValForCsv = (actionLog) => {
        if (actionLog.val === "") {
            return "";
        }

        let val = {};
        const valDict = JSON.parse(actionLog.val);
        switch (actionLog.event_name) {
            case "dictionary_upload":
            case "rag_detach":
            case "rag_attach_failed":
            case "rag_delete":
            case "rag_upload":
            case "user_prompt_delete":
            case "user_prompt_upload":
                val["ファイル名"] = valDict["asset_name"];
                break;
            case "structure_html":
                val["URL"] = valDict["url"];
                break;
            case "structure_document":
                val["ファイル名"] = valDict["file_name"];
                break;
            case "rag_attach":
                val["ファイル名"] = valDict["file_name"];
                val["チャンク分割"] = valDict["add_val"] === 1 ? "固定" : "可変";
                break;
            case "rag_test_create":
                val["ファイル名"] = valDict["asset_name"];
                break;
            case "rag_test_create_completed":
                val["ファイル名"] = valDict["asset_name"];
                break;
            case "playground_send":
                val["質問"] = valDict["question"];
                val["回答"] = valDict["answer"];
                break;
            case "appearance_update":
                val = parseUserSettingsForCsv(valDict);
                break;
            case "inquiry_update":
                val = parseInquirySettingsForCsv(valDict);
                break;
            case "log_filter":
                val = parseChatLogFilter(valDict);
                break;
            case "inquiry_filter":
                val = parseInquiryFilter(valDict);
                break;
            case "preview_display":
                val["プレビューURL"] = valDict["url"];
                break;
            case "account_view":
                val["ページ"] = valDict["page"];
                break;
        }

        const valText = JSON.stringify(val);
        const csvCell = `"${valText.replace(/"/g, '""')}"`;
        return csvCell;
    }

    /**
     * 操作ログのCSVを生成する
     * @param {Array<Object>} actionLogs 操作ログのリスト
     * @returns 操作ログのCSV
     */
    const generateCsv = (actionLogs) => {
        const headers = ["アカウント名", "イベント名", "操作日時", "内容"];
        const csvRows = [headers.join(",")];
        actionLogs.forEach((item) => {
            const dateObject = new Date(item.regdate);
            const year = dateObject.getFullYear();
            const month = dateObject.getMonth() + 1;
            const day = dateObject.getDate();
            const hours = String(dateObject.getHours()).padStart(2, '0');
            const minutes = String(dateObject.getMinutes()).padStart(2, '0');
            const seconds = String(dateObject.getSeconds()).padStart(2, '0');
            const val = getValForCsv(item);
            const row = [
                item.account_name,
                item.name,
                `${year}年${month}月${day}日 ${hours}:${minutes}:${seconds}`,
                val
            ];
            csvRows.push(row.join(","));
        });
        return csvRows.join("\n");
    };

    /**
     * CSVをダウンロードする
     */
    const handleCsvDownload = async () => {
        // 通信中の表示を出す
        dispatch(setLoading(true));
        const params = buildRequestParams(1, false, startDate, endDate, selectedTAccountIds, selectedEventNames);
        const { response } = await CallAdminActionLogListApi(params);

        // 通信中の表示を消す
        dispatch(setLoading(false));
        if (response && response.error_code === 0) {
            const csvData = generateCsv(response.action_log_list);
            // SJIS に変換して Blob を作成
            const sjisArray = Encoding.convert(csvData, { to: 'SJIS', type: 'array' });
            const uint8Array = new Uint8Array(sjisArray);
            const blob = new Blob([uint8Array], { type: 'text/csv;charset=Shift_JIS' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = Constants.ACTION_LOG_CSV_FILE_NAME;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }
        else {
            dispatch(setErrorModal({ show: true, text: response.error_detail }));
        }
    }

    /**
     * フィルターを初期状態に戻す
     * 「クリア」は絞り込みを外す操作なので、チェックは全解除ではなく全選択に戻す
     */
    const handleFilterClear = () => {
        setSelectedTAccountIds(Object.keys(accountNameDict));
        setSelectedEventNames(Object.keys(eventNameDict));
        const defaultStartDate = getDefaultStartDate();
        const defaultEndDate = getDefaultEndDate();
        setStartDateInput(formatDateTimeForInput(defaultStartDate));
        setEndDateInput(formatDateTimeForInput(defaultEndDate));
        setStartDate(defaultStartDate);
        setEndDate(defaultEndDate);
    }

    const handleEventNameChange = (id) => {
        setSelectedEventNames((prev) =>
            prev.includes(id)
                ? prev.filter((eventId) => eventId !== id)
                : [...prev, id]
        );
    };

    const handleAccountChange = (id) => {
        setSelectedTAccountIds((prev) =>
            prev.includes(id)
                ? prev.filter((accountId) => accountId !== id)
                : [...prev, id]
        );
    };

    /**
     * 「すべて」チェックを切り替える
     * @param {boolean} isAllSelected 切り替え前にすべて選択済みだったか
     */
    const handleToggleAllEventNames = (isAllSelected) => {
        setSelectedEventNames(isAllSelected ? [] : Object.keys(eventNameDict));
    };

    const handleToggleAllAccounts = (isAllSelected) => {
        setSelectedTAccountIds(isAllSelected ? [] : Object.keys(accountNameDict));
    };

    const closeDetailModal = () => {
        setIsShowDetailModal(false);
    };

    /**
     * 複数選択フィルター(先頭に「すべて」を持つチェックボックス群)を描画する
     * @param {Object} params
     * @param {string} params.label 条件名
     * @param {Object} params.optionDict 選択肢({値: 表示名})
     * @param {Array<string>} params.selectedValues 選択中の値
     * @param {Function} params.onToggle 個別チェックの切り替え
     * @param {Function} params.onToggleAll 「すべて」チェックの切り替え
     * @param {Object} params.detailsRef 領域外クリックで閉じるための参照
     * @param {string} params.containerClassName 幅調整用のクラス
     */
    const renderMultiSelectFilter = ({ label, optionDict, selectedValues, onToggle, onToggleAll, detailsRef, containerClassName }) => {
        const optionValues = Object.keys(optionDict);
        const isAllSelected = optionValues.length > 0 && selectedValues.length === optionValues.length;
        // 一部だけ選択されている状態。input は display:none のため ネイティブの indeterminate は効かず、見た目はクラスで作る
        const isPartialSelected = selectedValues.length > 0 && !isAllSelected;

        let summaryText = "未選択";
        if (isAllSelected) {
            summaryText = "すべて";
        }
        else if (selectedValues.length > 0) {
            summaryText = `${selectedValues.length}件選択中`;
        }

        return (
            <div className={"search-container " + containerClassName}>
                <span className="search-label">{label}</span>
                <details className="event-name-details" ref={detailsRef}>
                    <summary className="event-name-summary">
                        <span>{summaryText}</span>
                        <Icon icon="chevron-down" size={16} />
                    </summary>
                    <div className="event-name-options">
                        <label className="event-name-option event-name-option-all">
                            <input
                                type="checkbox"
                                checked={isAllSelected}
                                onChange={() => onToggleAll(isAllSelected)}
                            />
                            <div className={"checkbox-box" + (isPartialSelected ? " partial" : "")}>
                                <Icon className="checkbox-icon" icon={isPartialSelected ? "dash" : "check2"} size={16} />
                            </div>
                            <span>すべて</span>
                        </label>
                        {optionValues.map((value) => (
                            <label key={value} className="event-name-option">
                                <input
                                    type="checkbox"
                                    value={value}
                                    checked={selectedValues.includes(value)}
                                    onChange={() => onToggle(value)}
                                />
                                <div className="checkbox-box">
                                    <Icon className="checkbox-icon" icon="check2" size={16} />
                                </div>
                                <span>{optionDict[value]}</span>
                            </label>
                        ))}
                    </div>
                </details>
            </div>
        );
    };

    const renderListView = () => (
        <Stack>
            <Card className="base-card margin-bottom-32px p-4">
                <Stack className="width-full align-center spacing-16px">
                    <Row className="flex-both align-center width-full action-log-toolbar spacing-16px">
                        <Row className="flex-left align-center width-full date-area">
                            {renderMultiSelectFilter({
                                label: "アカウント名",
                                optionDict: accountNameDict,
                                selectedValues: selectedTAccountIds,
                                onToggle: handleAccountChange,
                                onToggleAll: handleToggleAllAccounts,
                                detailsRef: accountSelectRef,
                                containerClassName: "search-container-account",
                            })}
                            {renderMultiSelectFilter({
                                label: "イベント名",
                                optionDict: eventNameDict,
                                selectedValues: selectedEventNames,
                                onToggle: handleEventNameChange,
                                onToggleAll: handleToggleAllEventNames,
                                detailsRef: eventSelectRef,
                                containerClassName: "search-container-event",
                            })}
                            <div className="search-container search-container-datetime">
                                <span className="search-label">開始日時</span>
                                <input
                                    className="date-input"
                                    type="text"
                                    value={startDateInput}
                                    placeholder="yyyy-mm-dd HH:mm"
                                    onChange={(e) => {
                                        setStartDateInput(e.target.value);
                                        // 時刻を省略して打たれた場合はその日の始まりとして扱う
                                        const parsedDate = parseDateTimeInput(e.target.value, 0, 0);
                                        if (parsedDate) {
                                            setStartDate(parsedDate);
                                        }
                                    }}
                                    onClick={() => {
                                        setIsStartDialogVisible(true);
                                    }}
                                />
                                <Icon
                                    className="date-calendar-icon"
                                    icon="calendar"
                                />
                                {isStartDialogVisible && (
                                    <div className="calender-dialog" ref={startDialogRef}>
                                        <Card id="custom-date-range-picker" className="date-range-picker solid-card">
                                            <DatePicker
                                                dateFormat="yyyy/MM/dd HH:mm"
                                                selected={startDate}
                                                onChange={(date) => {
                                                    setStartDateInput(formatDateTimeForInput(date));
                                                    setStartDate(date);
                                                }}
                                                showTimeSelect
                                                timeFormat="HH:mm"
                                                timeIntervals={TIME_INTERVAL_MINUTES}
                                                timeCaption="時刻"
                                                maxDate={maxDate}
                                                inline
                                            />
                                        </Card>
                                    </div>
                                )}
                            </div>
                            <div className="search-container search-container-datetime">
                                <span className="search-label">終了日時</span>
                                <input
                                    className="date-input"
                                    type="text"
                                    value={endDateInput}
                                    placeholder="yyyy-mm-dd HH:mm"
                                    onChange={(e) => {
                                        setEndDateInput(e.target.value);
                                        // 時刻を省略して打たれた場合はその日の終わりとして扱う
                                        const parsedDate = parseDateTimeInput(e.target.value, MAX_HOUR, MAX_MINUTE);
                                        if (parsedDate) {
                                            setEndDate(parsedDate);
                                        }
                                    }}
                                    onClick={() => {
                                        setIsEndDialogVisible(true);
                                    }}
                                />
                                <Icon
                                    className="date-calendar-icon"
                                    icon="calendar"
                                />
                                {isEndDialogVisible && (
                                    <div className="calender-dialog" ref={endDialogRef}>
                                        <Card id="custom-date-range-picker" className="date-range-picker solid-card end-datetime-picker">
                                            {/* 時刻候補は injectTimes 側で組み立てるため、timeIntervals からの自動生成は1件に抑える */}
                                            <DatePicker
                                                dateFormat="yyyy/MM/dd HH:mm"
                                                selected={endDate}
                                                onChange={(date) => {
                                                    setEndDateInput(formatDateTimeForInput(date));
                                                    setEndDate(date);
                                                }}
                                                showTimeSelect
                                                timeFormat="HH:mm"
                                                timeIntervals={MINUTES_IN_DAY}
                                                timeCaption="時刻"
                                                injectTimes={endTimeOptions}
                                                maxDate={maxDate}
                                                inline
                                            />
                                        </Card>
                                    </div>
                                )}
                            </div>
                        </Row>
                        <Row className="flex-right align-center spacing-8px">
                            <Button className="secondary-border search-button" onClick={handleFilterClear}>クリア</Button>
                            <Button className="primary search-button" onClick={handleSearch}>検索</Button>
                            <Button className="reload-button" onClick={handleReload} disabled={isListLoading}>
                                <Icon icon="arrow-clockwise" size={20} />
                            </Button>
                        </Row>
                    </Row>

                    <FitList
                        list={actionLogListData}
                        header={true}
                        scrollbar={"scrollbar"}
                        isLoading={(isListLoading || (initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete)) ? true : false}
                    />
                    <Pagination
                        totalPages={maxPage}
                        currentPage={page}
                        onChange={handlePageChange}
                        isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                    />
                </Stack>
            </Card>
        </Stack>
    );

    /**
     * 対話ログのフィルター内容をパースする
     * @param {Object} val フィルター内容
     * @returns パース結果
     */
    const parseChatLogFilter = (val) => {
        const result = {};
        if ("page" in val) {
            result["ページ"] = val["page"];
        }
        if ("range_start_date" in val) {
            result["開始日"] = formatDate(val["range_start_date"]);
        }
        if ("range_end_date" in val) {
            result["終了日"] = formatDate(val["range_end_date"]);
        }
        if ("ip_address" in val) {
            result["IPアドレス"] = val["ip_address"];
        }
        if ("session_id" in val) {
            result["セッションID"] = val["session_id"];
        }
        if ("question" in val) {
            result["質問"] = val["question"];
        }
        if ("answer" in val) {
            result["回答"] = val["answer"];
        }
        return result;
    }

    /**
     * お問い合わせ履歴のフィルター内容をパースする
     * @param {Object} val フィルター内容
     * @returns パース結果
     */
    const parseInquiryFilter = (val) => {
        const result = {};
        if ("page" in val) {
            result["ページ"] = val["page"];
        }
        if ("range_start_date" in val) {
            result["開始日"] = formatDate(val["range_start_date"]);
        }
        if ("range_end_date" in val) {
            result["終了日"] = formatDate(val["range_end_date"]);
        }
        if ("inquiry_id" in val) {
            result["お問い合わせ番号"] = val["inquiry_id"];
        }
        return result;
    }

    /**
     * 表示設定の変更内容をパースする
     * @param {Object} val 変更内容
     * @returns パース結果
     */
    const parseUserSettings = (val) => {
        const result = {};
        if ("appearance_name" in val) {
            result["外見の名前"] = val["appearance_name"];
        }
        if ("button_color" in val) {
            result["ボタンカラー"] = val["button_color"];
        }
        if ("button_hover_color" in val) {
            result["ボタンホバーカラー"] = val["button_hover_color"];
        }
        if ("text_color" in val) {
            result["テキストカラー"] = val["text_color"];
        }
        if ("button_msg" in val) {
            result["ボタン表示"] = val["button_msg"];
        }
        if ("position" in val) {
            if (val["position"]["before"]) val["position"]["before"] = val["position"]["before"] === "left" ? "左下" : "右下";
            if (val["position"]["after"]) val["position"]["after"] = val["position"]["after"] === "left" ? "左下" : "右下";
            result["表示位置"] = val["position"];
        }
        if ("theme" in val) {
            if (val["theme"]["before"]) val["theme"]["before"] = val["theme"]["before"] === "light" ? "ライト" : "ダーク";
            if (val["theme"]["after"]) val["theme"]["after"] = val["theme"]["after"] === "light" ? "ライト" : "ダーク";
            result["カラーテーマ"] = val["theme"];
        }
        if ("is_typing_effect" in val) {
            if (val["is_typing_effect"]["before"] !== undefined) val["is_typing_effect"]["before"] = val["is_typing_effect"]["before"] == 1 ? "ON" : "OFF";
            if (val["is_typing_effect"]["after"] !== undefined) val["is_typing_effect"]["after"] = val["is_typing_effect"]["after"] == 1 ? "ON" : "OFF";
            result["文字送り表示"] = val["is_typing_effect"];
        }
        if ("open_mode" in val) {
            if (val["open_mode"]["before"]) val["open_mode"]["before"] = val["open_mode"]["before"] === "inline" ? "同タブ" : "別タブ";
            if (val["open_mode"]["after"]) val["open_mode"]["after"] = val["open_mode"]["after"] === "inline" ? "同タブ" : "別タブ";
            result["表示方法"] = val["open_mode"];
        }
        if ("dest_page_url" in val) {
            result["対話画面URL"] = val["dest_page_url"];
        }
        if ("welcome_msg" in val) {
            result["チャットWelcomeメッセージ"] = val["welcome_msg"];
        }
        if ("top_welcome_msg" in val) {
            result["トップWelcomeメッセージ"] = val["top_welcome_msg"];
        }
        if ("top_target_id" in val) {
            result["ターゲットID"] = val["top_target_id"];
        }
        return result;
    }

    /**
     * CSV向けに表示設定の変更内容をパースする
     * @param {Object} val 変更内容
     * @returns パース結果
     */
    const parseUserSettingsForCsv = (val) => {
        const result = {};
        if ("appearance_name" in val) {
            result["外見の名前"] = val["appearance_name"]["after"];
        }
        if ("button_color" in val) {
            result["ボタンカラー"] = val["button_color"]["after"];
        }
        if ("button_hover_color" in val) {
            result["ボタンホバーカラー"] = val["button_hover_color"]["after"];
        }
        if ("text_color" in val) {
            result["テキストカラー"] = val["text_color"]["after"];
        }
        if ("button_msg" in val) {
            result["ボタン表示"] = val["button_msg"]["after"];
        }
        if ("position" in val) {
            result["表示位置"] = val["position"]["after"] === "left" ? "左下" : "右下";
        }
        if ("theme" in val) {
            result["カラーテーマ"] = val["theme"]["after"] === "light" ? "ライト" : "ダーク";
        }
        if ("is_typing_effect" in val) {
            result["文字送り表示"] = val["is_typing_effect"]["after"] == 1 ? "ON" : "OFF";
        }
        if ("open_mode" in val) {
            result["表示方法"] = val["open_mode"]["after"] === "inline" ? "同タブ" : "別タブ";
        }
        if ("dest_page_url" in val) {
            result["対話画面URL"] = val["dest_page_url"]["after"];
        }
        if ("welcome_msg" in val) {
            result["チャットWelcomeメッセージ"] = val["welcome_msg"]["after"];
        }
        if ("top_welcome_msg" in val) {
            result["トップWelcomeメッセージ"] = val["top_welcome_msg"]["after"];
        }
        if ("top_target_id" in val) {
            result["ターゲットID"] = val["top_target_id"]["after"];
        }
        return result;
    }

    /**
     * 有人引き継ぎ設定の変更内容をパースする
     * @param {Object} val 変更内容
     * @returns パース結果
     */
    const parseInquirySettings = (val) => {
        const result = {};
        if ("is_enabled" in val) {
            if (val["is_enabled"]["before"] !== "") val["is_enabled"]["before"] = val["is_enabled"]["before"] === 1 ? "有効" : "無効";
            if (val["is_enabled"]["after"] !== "") val["is_enabled"]["after"] = val["is_enabled"]["after"] === 1 ? "有効" : "無効";
            result["有効フラグ"] = val["is_enabled"]
        }
        if ("is_webhook_enabled" in val) {
            // 0はfalsyのため値の有無ではなくnull判定にする(0のまま表示されるのを防ぐ)
            if (val["is_webhook_enabled"]["before"] !== "") val["is_webhook_enabled"]["before"] = val["is_webhook_enabled"]["before"] === 1 ? "有効" : "無効";
            if (val["is_webhook_enabled"]["after"] !== "") val["is_webhook_enabled"]["after"] = val["is_webhook_enabled"]["after"] === 1 ? "有効" : "無効";
            result["Webhookの有効フラグ"] = val["is_webhook_enabled"]
        }
        if ("webhook_url" in val) {
            result["WebhookのURL"] = val["webhook_url"];
        }
        if ("button_label" in val) {
            result["ボタン表示名"] = val["button_label"];
        }
        if ("mail_address" in val) {
            result["メールアドレス"] = val["mail_address"];
        }
        if ("mail_subject" in val) {
            result["メール件名"] = val["mail_subject"];
        }
        if ("form_description" in val) {
            result["フォーム説明文"] = val["form_description"];
        }
        if ("privacy_policy_url" in val) {
            result["プライバシーポリシー"] = val["privacy_policy_url"];
        }
        if ("thanks_url" in val) {
            result["サンクスページのURL"] = val["thanks_url"];
        }
        if ("thanks_heading" in val) {
            result["見出し"] = val["thanks_heading"];
        }
        if ("thanks_text" in val) {
            result["本文"] = val["thanks_text"];
        }
        if ("summary_format_asset_name" in val) {
            result["要約フォーマット"] = val["summary_format_asset_name"];
        }
        if ("form_items" in val) {
            result["フォーム項目定義"] = {
                "before": val["form_items"]["before"] ? JSON.parse(val["form_items"]["before"]) : [],
                "after": val["form_items"]["after"] ? JSON.parse(val["form_items"]["after"]) : []
            };
        }
        return result;
    }

    /**
     * CSV向けに有人引き継ぎ設定の変更内容をパースする
     * @param {Object} val 変更内容
     * @returns パース結果
     */
    const parseInquirySettingsForCsv = (val) => {
        const result = {};
        if ("is_enabled" in val) {
            result["有効フラグ"] = val["is_enabled"]["after"] === 1 ? "有効" : "無効";
        }
        if ("is_webhook_enabled" in val) {
            result["Webhookの有効フラグ"] = val["is_webhook_enabled"]["after"] === 1 ? "有効" : "無効";
        }
        if ("webhook_url" in val) {
            result["WebhookのURL"] = val["webhook_url"]["after"];
        }
        if ("button_label" in val) {
            result["ボタン表示名"] = val["button_label"]["after"];
        }
        if ("mail_address" in val) {
            result["メールアドレス"] = val["mail_address"]["after"];
        }
        if ("mail_subject" in val) {
            result["メール件名"] = val["mail_subject"]["after"];
        }
        if ("form_description" in val) {
            result["フォーム説明文"] = val["form_description"]["after"];
        }
        if ("privacy_policy_url" in val) {
            result["プライバシーポリシー"] = val["privacy_policy_url"]["after"];
        }
        if ("thanks_url" in val) {
            result["サンクスページのURL"] = val["thanks_url"]["after"];
        }
        if ("thanks_heading" in val) {
            result["見出し"] = val["thanks_heading"]["after"];
        }
        if ("thanks_text" in val) {
            result["本文"] = val["thanks_text"]["after"];
        }
        if ("summary_format_asset_name" in val) {
            result["要約フォーマット"] = val["summary_format_asset_name"]["after"];
        }
        if ("form_items" in val) {
            result["フォーム項目定義"] = val["form_items"]["after"];
        }
        return result;
    }

    const INQUIRY_FORM_TYPE_LABELS = {
        "text": "テキスト（1行）",
        "textarea": "テキストエリア（複数行）",
        "email": "メールアドレス",
        "tel": "電話番号",
        "alphanumeric": "英数のみ"
    };

    const replaceInquiryFormLabel = (formItems) => {
        formItems.forEach((formItem) => {
            formItem["required"] = formItem["required"] ? "必須" : "任意";
            formItem["type"] = INQUIRY_FORM_TYPE_LABELS[formItem["type"]];
        });
        return formItems;
    }

    // フォーム項目定義のグリッド列(変更前後を上下2段で並べる)
    const INQUIRY_FORM_COLUMNS = [
        { key: "no", title: "#" },
        { key: "required", title: "必須" },
        { key: "name", title: "管理名" },
        { key: "label", title: "見出し" },
        { key: "type", title: "種別" },
        { key: "placeholder", title: "プレースホルダ" },
    ];

    /**
     * フォーム項目定義の変更前後をグリッドで描画する
     * 上段に変更前、下段に変更後を並べ、変わっていない項目は下段のみ表示する
     * @param {Array<Object>} beforeDatas 変更前のフォーム項目
     * @param {Array<Object>} afterDatas 変更後のフォーム項目
     */
    const renderInquiryForm = (beforeDatas, afterDatas, hasContentAbove) => {
        const befores = replaceInquiryFormLabel([...beforeDatas]);
        const afters = replaceInquiryFormLabel([...afterDatas]);
        const rowCount = Math.max(befores.length, afters.length);

        const rows = [];
        for (let i = 0; i < rowCount; i++) {
            rows.push({
                no: i + 1,
                before: i < befores.length ? befores[i] : null,
                after: i < afters.length ? afters[i] : null,
            });
        }

        const getCellValue = (formItem, columnKey, no) => {
            if (columnKey === "no") {
                return String(no);
            }
            return formItem ? (formItem[columnKey] || "") : "";
        };

        return (
            // 上に定義リストがある場合だけ、定義リストの行間と同じ破線で区切る
            <Stack className={"spacing-8px inquiry-form-section" + (hasContentAbove ? " inquiry-form-section-divided" : "")}>
                <div className="detail-grid-heading">
                    <span>フォーム項目定義</span>
                    {/* 2段になっているセルの読み方が分かるよう、変更前後と同じ色で凡例を出す */}
                    <span className="detail-grid-legend">
                        <span className="detail-grid-legend-before">上段 = 変更前</span>
                        <span className="detail-grid-legend-separator">/</span>
                        <span className="detail-grid-legend-after">下段 = 変更後</span>
                    </span>
                </div>
                <div className="inquiry-form-grid">
                    <div className="inquiry-form-grid-head">
                        {INQUIRY_FORM_COLUMNS.map((column) => (
                            <div key={column.key} className="inquiry-form-grid-cell">{column.title}</div>
                        ))}
                    </div>
                    {rows.map((row) => (
                        <div key={row.no} className="inquiry-form-grid-row">
                            {INQUIRY_FORM_COLUMNS.map((column) => {
                                const beforeValue = getCellValue(row.before, column.key, row.no);
                                const afterValue = getCellValue(row.after, column.key, row.no);
                                // 変わっていないセルと、削除されて変更後が無いセルは1行だけ表示する
                                // (2段になるのは値が置き換わったセルのみ)
                                const isChanged = beforeValue !== afterValue;
                                // 変更後が無い(値を消した/項目ごと削除した)場合だけ打ち消し線を引く
                                // 値が置き換わった場合は変更後が下に並ぶため、打ち消し線は付けない
                                const isRemoved = isChanged && !afterValue;
                                return (
                                    <div key={column.key} className="inquiry-form-grid-cell">
                                        {isChanged && beforeValue &&
                                            <div className={"inquiry-form-grid-before" + (isRemoved ? " inquiry-form-grid-removed" : "")}>{beforeValue}</div>
                                        }
                                        {afterValue &&
                                            <div className="inquiry-form-grid-after">{afterValue}</div>
                                        }
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </Stack>
        );
    }

    /**
     * 詳細項目に表示する値があるか判定する
     * 0や false も表示対象のため、値の有無はnull・空文字だけで判定する
     */
    const hasValue = (value) => value !== undefined && value !== null && value !== "";

    /**
     * 表示する値がある項目だけに絞る
     * 変更前後どちらも無い項目は操作対象ではないため行ごと出さない(account_editで変更していない項目など)
     */
    const filterVisibleItems = (items) => items.filter((item) => hasValue(item.before) || hasValue(item.after));

    /**
     * 詳細項目を定義リストとして描画する
     * @param {Array<Object>} items [{ key, before, after }] beforeがある項目は「変更前 → 変更後」で表示する
     */
    const renderDetailItems = (items) => {
        const visibleItems = filterVisibleItems(items);
        // 変更前を持つ項目が1つも無い表(フィルター内容や追加・削除の内容など)は、
        // 変更前と対比させる必要がないため値を太字にしない
        const isDiff = visibleItems.some((item) => hasValue(item.before));
        return (
        <div className={"detail-item-list" + (isDiff ? " detail-item-list-diff" : "")}>
            {visibleItems.map((item, index) => {
                // 未登録から入力した場合は変更前が無いため、矢印を出さずに入力値だけを見せる
                const hasBefore = hasValue(item.before);
                // 入力内容を消した場合は変更後が無いため、変更前を打ち消し線だけで見せる
                const hasAfter = hasValue(item.after);
                return (
                    <div key={index} className="detail-item">
                        <div className="detail-item-key">{item.key}</div>
                        <div className="detail-item-value">
                            {hasBefore &&
                                <span className={"detail-item-before" + (hasAfter ? "" : " detail-item-removed")}>{item.before}</span>
                            }
                            {hasBefore && hasAfter &&
                                <span className="detail-item-arrow">→</span>
                            }
                            {hasAfter &&
                                <span className="detail-item-after">{item.after}</span>
                            }
                        </div>
                    </div>
                );
            })}
        </div>
        );
    };

    /**
     * 見出しを付けて詳細項目を描画する
     * @param {string} heading 見出し
     * @param {Array<Object>} items 詳細項目
     */
    const renderDetailSection = (heading, items) => {
        // 表示できる項目が1件も無い場合は、見出しだけが浮かないよう区画ごと出さない
        if (filterVisibleItems(items).length === 0) return null;
        return (
            <>
                <div className="detail-section-heading">{heading}</div>
                {renderDetailItems(items)}
            </>
        );
    };

    // 変更前後を持たないイベントは、見出しと項目の作り方だけを定義する
    const DETAIL_SECTIONS = {
        dictionary_upload:  { heading: "変更した内容",         items: (val) => [{ key: "ファイル名", after: val["asset_name"] }] },
        structure_html:     { heading: "構造化した内容",       items: (val) => [{ key: "URL", after: val["url"] }] },
        structure_document: { heading: "構造化した内容",       items: (val) => [{ key: "ファイル名", after: val["file_name"] }] },
        rag_attach:         { heading: "学習した内容",         items: (val) => [
            { key: "ファイル名", after: val["asset_name"] },
            { key: "チャンク分割", after: val["add_val"] === 1 ? "固定" : "可変" },
        ] },
        rag_detach:         { heading: "学習解除した内容",     items: (val) => [{ key: "ファイル名", after: val["asset_name"] }] },
        rag_attach_failed:  { heading: "学習に失敗した内容",   items: (val) => [{ key: "ファイル名", after: val["asset_name"] }] },
        rag_delete:         { heading: "削除した内容",         items: (val) => [{ key: "ファイル名", after: val["asset_name"] }] },
        rag_upload:         { heading: "アップロードした内容", items: (val) => [{ key: "ファイル名", after: val["asset_name"] }] },
        user_prompt_delete: { heading: "削除した内容",         items: (val) => [{ key: "ファイル名", after: val["asset_name"] }] },
        user_prompt_upload: { heading: "アップロードした内容", items: (val) => [{ key: "ファイル名", after: val["asset_name"] }] },
        rag_test_create:    { heading: "作成した内容",         items: (val) => [{ key: "ファイル名", after: val["asset_name"] }] },
        rag_test_create_completed: { heading: "作成した内容",  items: (val) => [{ key: "ファイル名", after: val["asset_name"] }] },
        playground_send:    { heading: "対話内容",             items: (val) => [
            { key: "送信メッセージ", after: val["question"] },
            { key: "返答メッセージ", after: val["answer"] },
        ] },
        account_add:        { heading: "追加した内容",         items: (val) => [
            { key: "メールアドレス", after: val["email"] },
            { key: "権限", after: Constants.UserAuthType[val["auth"]] || val["auth"] },
        ] },
        account_edit:       { heading: "変更した内容",         items: (val) => [
            { key: "アカウント名", after: val["account_name"] },
            { key: "メールアドレス", after: val["email"] },
            { key: "パスワード", after: val["password"] ? "変更あり" : "" },
            { key: "権限", after: Constants.UserAuthType[val["auth"]] || val["auth"] },
        ] },
        account_delete:     { heading: "削除した内容",         items: (val) => [
            { key: "アカウント名", after: val["account_name"] },
            { key: "メールアドレス", after: val["email"] },
            { key: "権限", after: Constants.UserAuthType[val["auth"]] || val["auth"] },
        ] },
        account_unlock_login: { heading: "変更した内容",       items: (val) => [
            { key: "アカウント名", after: val["account_name"] },
            { key: "ログイン制限", after: val["unlock_login"] ? "解除" : "" },
        ] },
        preview_display:    { heading: "表示した内容",         items: (val) => [{ key: "プレビューURL", after: val["url"] }] },
        account_view:       { heading: "表示した内容",         items: (val) => [{ key: "ページ", after: val["page"] }] },
    };

    // フィルター条件だけを持つイベントは、条件のパース処理だけを定義する
    const FILTER_PARSERS = {
        log_filter: parseChatLogFilter,
        inquiry_filter: parseInquiryFilter,
    };

    const renderActionVal = (actionLog) => {
        const actionVal = JSON.parse(actionLog.val);

        const detailSection = DETAIL_SECTIONS[actionLog.event_name];
        if (detailSection) {
            return renderDetailSection(detailSection.heading, detailSection.items(actionVal));
        }

        const filterParser = FILTER_PARSERS[actionLog.event_name];
        if (filterParser) {
            const filterVal = filterParser(actionVal);
            return renderDetailSection("フィルター内容", Object.entries(filterVal).map(([key, value]) => ({
                key: key,
                after: value,
            })));
        }

        if (actionLog.event_name === "appearance_update") {
            const userSettings = parseUserSettings(actionVal);
            return renderDetailSection("変更した内容", Object.entries(userSettings).map(([key, value]) => ({
                key: key,
                before: value.before,
                after: value.after,
            })));
        }

        if (actionLog.event_name === "inquiry_update") {
            const inquirySettings = parseInquirySettings(actionVal);
            // フォーム項目定義は項目数が多く列も揃えたいため、定義リストとは分けてグリッドで表示する
            const items = filterVisibleItems(Object.entries(inquirySettings)
                .filter(([key]) => key !== "フォーム項目定義")
                .map(([key, value]) => ({ key: key, before: value.before, after: value.after })));
            return (
                <>
                    {renderDetailSection("変更した内容", items)}
                    {Object.hasOwn(inquirySettings, "フォーム項目定義") &&
                        renderInquiryForm(
                            inquirySettings["フォーム項目定義"]["before"],
                            inquirySettings["フォーム項目定義"]["after"],
                            items.length > 0
                        )
                    }
                </>
            );
        }

        return <span>{actionLog.val}</span>
    }

    /**
     * 操作ログ詳細のポップアップを描画する
     * 閲覧するだけのため、×ボタン・背景クリック・Escapeのいずれでも閉じられるようにする
     */
    const renderDetailModal = () => {
        if (!isShowDetailModal) {
            return null;
        }

        return (
            <div className="modal-overlay" onClick={closeDetailModal}>
                <div className="modal-content faq-config-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <div className="text-size-h2">操作ログ詳細</div>
                        <CloseButton onClick={closeDetailModal} />
                    </div>
                    <div className="modal-body faq-config-body">
                        <Card className="faq-config-card">
                            <Stack className="spacing-16px">
                                <Row className="detail-summary-row align-top spacing-24px">
                                    <Stack className="detail-summary-cell spacing-4px">
                                        <div className="detail-label text-bold">アカウント名</div>
                                        <div className="detail-value">{detailActionLog ? detailActionLog.account_name : "-"}</div>
                                    </Stack>
                                    <Stack className="detail-summary-cell spacing-4px">
                                        <div className="detail-label text-bold">イベント名</div>
                                        <div className="detail-value detail-value-pre">{detailActionLog ? detailActionLog.name : "-"}</div>
                                    </Stack>
                                    <Stack className="detail-summary-cell spacing-4px">
                                        <div className="detail-label text-bold">操作日時</div>
                                        <div className="detail-value">{detailActionLog ? formatDateTime(detailActionLog.regdate) : "-"}</div>
                                    </Stack>
                                </Row>
                                {detailActionLog && detailActionLog.val && (
                                    <>
                                        <hr />
                                        <Stack className="spacing-4px">
                                            {renderActionVal(detailActionLog)}
                                        </Stack>
                                    </>
                                )}
                            </Stack>
                        </Card>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <PageContent pageId="AdminUserActionLog">
            <div ref={topRef}></div>
            <PageHeader>
                <Row className="flex-both align-center width-full">
                    <Stack className="spacing-4px">
                        <div className="page-header-title">操作ログ</div>
                        <div className="page-header-detail">管理画面で行われた操作の履歴を確認できます。</div>
                    </Stack>
                    <Button
                        className="header-download-button primary"
                        onClick={handleCsvDownload}
                        disabled={initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete || isEmptyFilter()}
                    >
                        <Row className="flex-center spacing-8px width-full">
                            <Icon className="label-icon" icon="download" size={18} />
                            <div>CSVダウンロード</div>
                        </Row>
                    </Button>
                </Row>
            </PageHeader>
            <PageWrapper>
                <PageContainer>
                    {renderListView()}
                    <Button className="scroll-button primary" onClick={() => {scrollToRef(topRef)}}>
                        <Icon icon="chevron-up" size={28} className="copy-icon"/>
                    </Button>
                </PageContainer>
            </PageWrapper>
            {/* 詳細ポップアップ */}
            {renderDetailModal()}
        </PageContent>
    );
};

export default AdminUserActionLog;
